import { useEffect } from 'react';
import {
  Link,
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { FISCAL_DOCUMENT_STATES, type FiscalDocumentState } from '@optefiscal/fiscal-domain';
import { FiscalStateBadge } from './components/FiscalStateBadge.js';
import { TaxIdInspector } from './components/TaxIdInspector.js';
import { LoadingState, UnauthorizedState } from './components/States.js';
import { BootErrorBoundary } from './components/BootErrorBoundary.js';
import { SessionProvider, useSession } from './lib/session.js';
import { LoginPage } from './pages/Login.js';
import { DashboardPage } from './pages/Dashboard.js';
import { EmissoesNfePage, EmissoesNfsePage } from './pages/Emissoes.js';
import { DocumentoDetalhePage, DocumentosPage } from './pages/Documentos.js';
import {
  CertificadosPage,
  ConfiguracoesPage,
  DiagnosticosPage,
  EventosPage,
  IntegracoesPage,
  RecepcaoPage,
} from './pages/Operacao.js';

/**
 * Shell do OpteFiscal 2.0.
 *
 * Módulos conforme o Manual de Identidade Visual, seção 09 (Interface do
 * produto) e o Plano Oficial, seção 4 (Visão funcional).
 *
 * O QUE MUDOU NO G7
 *
 *   Até o G6 os módulos fiscais apareciam BLOQUEADOS, com o gate que os
 *   liberava. Os que existem agora deixaram de ser promessa e viraram rota; os
 *   que continuam sem backend continuam marcados, e a marca diz qual gate falta.
 *
 *   A regra não mudou: nenhuma tela promete capacidade fiscal inexistente.
 */

interface NavItem {
  readonly label: string;
  readonly to?: string;
  readonly gate?: string;
}

const NAV_PRIMARY: readonly NavItem[] = [
  { label: 'Painel Fiscal', to: '/' },
  { label: 'Documentos', to: '/documentos' },
  { label: 'Identificador fiscal', to: '/identificador' },
  { label: 'Fundação', to: '/fundacao' },
];

const NAV_FISCAL: readonly NavItem[] = [
  { label: 'NF-e', to: '/emissoes/nfe' },
  { label: 'NFS-e', to: '/emissoes/nfse' },
  { label: 'DF-e recebidos', to: '/recepcao' },
  { label: 'Eventos fiscais', to: '/eventos' },
  { label: 'Certificados', to: '/certificados' },
  { label: 'Laboratório Fiscal', gate: 'G9' },
];

const NAV_SISTEMA: readonly NavItem[] = [
  { label: 'Integrações', to: '/integracoes' },
  { label: 'Diagnósticos', to: '/diagnosticos' },
  { label: 'Configurações', to: '/configuracoes' },
];

function NavGroup({ title, items }: { title: string; items: readonly NavItem[] }) {
  return (
    <div>
      <div className="of-nav__group-title">{title}</div>
      <nav className="of-nav">
        {items.map((item) =>
          item.to ? (
            <Link key={item.label} to={item.to} className="of-nav__item">
              <span className="of-nav__label">{item.label}</span>
            </Link>
          ) : (
            <span
              key={item.label}
              className="of-nav__item of-nav__item--locked"
              aria-disabled="true"
              title={`Disponível no gate ${item.gate}`}
            >
              <span className="of-nav__label">{item.label}</span>
              <span className="of-nav__gate">{item.gate}</span>
            </span>
          ),
        )}
      </nav>
    </div>
  );
}

/**
 * O seletor de identidade fiscal.
 *
 * Ele só aparece com mais de uma identidade autorizada. Com uma só, um `select`
 * de um item é ruído; com duas, ele é a coisa mais importante da barra — todo
 * dado da tela abaixo pertence à identidade escolhida aqui.
 */
function IdentitySwitch() {
  const { identities, selected, select } = useSession();
  if (selected === null) return null;

  if (identities.length === 1) {
    return (
      <div className="of-identity">
        <span className="of-identity__name">{selected.legal_name}</span>
        <span className="of-identity__tax-id of-mono">{selected.tax_id}</span>
      </div>
    );
  }

  return (
    <label className="of-identity">
      <span className="of-identity__label">Identidade fiscal</span>
      <select
        className="of-identity__select"
        value={selected.id}
        onChange={(e) => select(e.target.value)}
      >
        {identities.map((i) => (
          <option key={i.id} value={i.id}>
            {i.legal_name} — {i.tax_id}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * O ambiente exibido é o dos DOCUMENTOS, não o do build.
 *
 * O Plano Oficial (6.3) manda tornar o ambiente visível em toda operação
 * sensível. Até o G6 a barra mostrava `homologation` fixo, que era verdade
 * quando nada tinha sido emitido.
 *
 * Emitir em produção mudou isso. Um selo fixo de homologação sobre notas reais
 * seria a confusão de ambiente que o 2.0 existe para impedir — então ele passou
 * a ser omitido aqui e a viver em cada linha e em cada detalhe, onde é fato
 * sobre aquele documento.
 */
function Shell() {
  const { session, signOut } = useSession();

  return (
    <div className="of-shell">
      <aside className="of-sidebar">
        <div className="of-sidebar__brand">
          {/* Ícone oficial, nunca redesenhado. Manual, seções 03 e 14. */}
          <img src="/optefiscal-icone.svg" alt="" width={40} height={40} />
          <div className="of-sidebar__wordmark">
            OpteFiscal
            <span>Documentos fiscais</span>
          </div>
        </div>

        <NavGroup title="Plataforma" items={NAV_PRIMARY} />
        <NavGroup title="Capacidades fiscais" items={NAV_FISCAL} />
        <NavGroup title="Sistema" items={NAV_SISTEMA} />
      </aside>

      <div className="of-main">
        <header className="of-topbar">
          {/*
            Sistema de assinaturas (Manual, seção 03): a marca responde ao
            contexto sem mudar de identidade.

            Em tela larga usa-se a assinatura completa; em tela estreita usa-se
            o ÍCONE oficial. Encolher a assinatura completa abaixo do mínimo de
            220 px seria violar a seção 06 — trocar de assinatura é o
            comportamento previsto pelo Manual.
          */}
          <img
            className="of-topbar__logo of-topbar__logo--full"
            src="/optefiscal-logo-oficial.svg"
            alt="OpteFiscal — Documentos Fiscais"
          />
          <img
            className="of-topbar__logo--icon"
            src="/optefiscal-icone.svg"
            alt="OpteFiscal"
            width={36}
            height={36}
          />

          <div className="of-topbar__spacer" />
          <IdentitySwitch />
          <div className="of-topbar__user">
            <span className="of-topbar__email">{session?.user.email}</span>
            <button className="of-button of-button--ghost" type="button" onClick={() => void signOut()}>
              Sair
            </button>
          </div>
        </header>
        <main className="of-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * O portão. Uma pergunta de cada vez, na ordem do §53.
 *
 *   carregando?          espera
 *   sem sessão?          login
 *   sessão e sem acesso? a frase do §62
 *   com acesso?          o console
 *
 * O `/login` fica FORA do shell: uma tela de autenticação com a navegação do
 * produto atrás sugere que já se está dentro dele.
 */
function Gate() {
  const { state } = useSession();
  const naRotaDeLogin = useRouterState({
    select: (s) => s.location.pathname === '/login',
  });

  if (state === 'loading') {
    return <LoadingState label="Restaurando sessão…" />;
  }

  if (state === 'anonymous') {
    return <LoginPage />;
  }

  /*
   * Autenticado e ainda em /login: sai de lá.
   *
   * Quem acabou de entrar continuaria vendo o formulário — a sessão existe e a
   * URL não sabe. A navegação acontece num efeito porque redirecionar durante a
   * renderização é atualizar o roteador no meio da árvore que ele está pintando.
   */
  if (naRotaDeLogin) {
    return <RedirectToDashboard />;
  }

  if (state === 'authenticated_without_access' || state === 'error') {
    return (
      <div className="of-standalone">
        <UnauthorizedState />
        <SignOutLink />
      </div>
    );
  }

  return <Shell />;
}

function RedirectToDashboard() {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate({ to: '/' });
  }, [navigate]);
  return <LoadingState label="Entrando…" />;
}

function SignOutLink() {
  const { signOut } = useSession();
  return (
    <button className="of-button of-button--ghost" type="button" onClick={() => void signOut()}>
      Sair
    </button>
  );
}

// ---------------------------------------------------------------------------
// Páginas herdadas do G1 — continuam válidas e continuam aqui (§36)
// ---------------------------------------------------------------------------

function Identificador() {
  return (
    <>
      <div>
        <h1 className="of-page-title">Identificador fiscal</h1>
        <p className="of-page-subtitle">
          O OpteFiscal 2.0 nasce preparado para o CNPJ alfanumérico. Banco, API, SDK, XML,
          providers, parsers, buscas, máscaras, logs e validações usam a mesma representação textual
          canônica.
        </p>
      </div>
      <TaxIdInspector />
      <section className="of-card">
        <h2 className="of-card__title">Exemplos de referência</h2>
        <div style={{ display: 'grid', gap: 'var(--of-space-3)' }}>
          <div>
            <span className="of-label">Alfanumérico</span>
            <div className="of-mono">12.ABC.345/01DE-35</div>
          </div>
          <div>
            <span className="of-label">Numérico legado</span>
            <div className="of-mono">11.222.333/0001-81</div>
          </div>
          <div>
            <span className="of-label">Com zeros à esquerda</span>
            <div className="of-mono">00.000.000/0001-91</div>
          </div>
        </div>
      </section>
    </>
  );
}

function Fundacao() {
  const garantias = [
    'Banco novo, reproduzível do zero pelas migrations versionadas do repositório.',
    'RLS fail-closed desde a primeira migration, com cross-tenant testado em PostgreSQL real.',
    'CNPJ armazenado como texto canônico. Nenhum bigint, numeric ou coerção numérica.',
    'Produção e homologação isoladas: o ambiente faz parte da chave lógica e é imutável.',
    'Série e próximo número são estado operacional: cadastro nunca os altera.',
    'Nenhum fallback fiscal silencioso. Erro é explícito, classificado e rastreável.',
    'Toda operação carrega request id, trace id e chave de idempotência.',
    'Auditoria append-only. Segredos nunca aparecem em resposta, log ou webhook.',
  ];

  return (
    <>
      <div>
        <h1 className="of-page-title">Fundação</h1>
        <p className="of-page-subtitle">
          O primeiro objetivo do projeto não é emitir a primeira nota. É criar uma base em que uma
          emissão futura não possa corromper ambiente, numeração, identidade, certificado ou
          isolamento.
        </p>
      </div>
      <section className="of-card">
        <h2 className="of-card__title">Garantias já em vigor</h2>
        <ul className="of-list">
          {garantias.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
      <section className="of-card">
        <h2 className="of-card__title">Estados fiscais</h2>
        <p className="of-card__hint">
          Cor semântica não substitui texto, código ou estado persistido. Todo indicador carrega
          rótulo, forma de ícone própria e, quando existir, o código oficial.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--of-space-2)' }}>
          {FISCAL_DOCUMENT_STATES.map((state: FiscalDocumentState) => (
            <FiscalStateBadge key={state} state={state} />
          ))}
        </div>
        <p className="of-footnote" style={{ marginTop: 'var(--of-space-4)' }}>
          <strong>Estado desconhecido</strong> é um estado real: um timeout do autorizador não
          significa &ldquo;não emitiu&rdquo;. Nada é retransmitido antes de consultar.
        </p>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Rotas — §11
// ---------------------------------------------------------------------------

const rootRoute = createRootRoute({ component: Gate });

/*
 * Cada rota é declarada por extenso, e a repetição é o preço de um tipo.
 *
 * Uma função auxiliar `rota(path, component)` deixaria isto em catorze linhas —
 * e apagaria o tipo literal do caminho. O TanStack Router constrói a tabela de
 * rotas em tempo de compilação a partir dessas literais: sem elas,
 * `<Link to="/documentos/$id" params={{ id }} />` deixa de ser verificado, e um
 * link quebrado passa a ser descoberto clicando.
 */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});
const nfeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/emissoes/nfe',
  component: EmissoesNfePage,
});
const nfseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/emissoes/nfse',
  component: EmissoesNfsePage,
});
const recepcaoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recepcao',
  component: RecepcaoPage,
});
const eventosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/eventos',
  component: EventosPage,
});
const documentosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documentos',
  component: DocumentosPage,
});
const documentoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documentos/$id',
  component: DocumentoDetalhePage,
});
const certificadosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/certificados',
  component: CertificadosPage,
});
const integracoesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/integracoes',
  component: IntegracoesPage,
});
const diagnosticosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/diagnosticos',
  component: DiagnosticosPage,
});
const configuracoesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/configuracoes',
  component: ConfiguracoesPage,
});
const identificadorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/identificador',
  component: Identificador,
});
const fundacaoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fundacao',
  component: Fundacao,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  nfeRoute,
  nfseRoute,
  recepcaoRoute,
  eventosRoute,
  documentosRoute,
  documentoRoute,
  certificadosRoute,
  integracoesRoute,
  diagnosticosRoute,
  configuracoesRoute,
  identificadorRoute,
  fundacaoRoute,
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return (
    <BootErrorBoundary>
      <SessionProvider>
        <RouterProvider router={router} />
      </SessionProvider>
    </BootErrorBoundary>
  );
}
